import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { jobUrlSegment } from '../../lib/slug';
import Button from '../../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import Input from '../../components/ui/Input';
import Textarea from '../../components/ui/Textarea';
import Select from '../../components/ui/Select';

export default function JobTemplatesPage() {
  const navigate = useNavigate();
  const [agencyOrgId, setAgencyOrgId] = useState('');
  const [brands, setBrands] = useState<{ id: string; name: string }[]>([]);
  const [templates, setTemplates] = useState<any[]>([]);
  const [createCorp, setCreateCorp] = useState('');
  const [name, setName] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const [me, orgs] = await Promise.all([api.get('/auth/me'), api.get('/organizations')]);
        const agency = me.data.memberships?.find((m: any) => m.organization?.type === 'AGENCY');
        if (agency) setAgencyOrgId(agency.organizationId);
        const corps = (orgs.data || []).filter((o: any) => o.type === 'BRAND');
        setBrands(corps);
        if (corps[0]) setCreateCorp(corps[0].id);
      } catch (e) {
        console.error(e);
      }
    })();
  }, []);

  const load = async () => {
    if (!agencyOrgId) return;
    const res = await api.get('/job-templates', { params: { organizationId: agencyOrgId } });
    setTemplates(res.data);
  };

  useEffect(() => {
    load();
  }, [agencyOrgId]);

  const createTemplate = async () => {
    if (!agencyOrgId || !name.trim() || !title.trim() || !description.trim()) return;
    await api.post('/job-templates', {
      organizationId: agencyOrgId,
      name: name.trim(),
      title: title.trim(),
      description: description.trim(),
      estimatedKOL: 10,
    });
    setName('');
    setTitle('');
    setDescription('');
    load();
  };

  const createJobFrom = async (templateId: string) => {
    if (!createCorp) {
      alert('Select a brand');
      return;
    }
    const res = await api.post(`/job-templates/${templateId}/create-job`, {
      brandOrgId: createCorp,
    });
    navigate(`/agency/campaigns/${jobUrlSegment(res.data)}`);
  };

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h1 className="text-[2.25rem] font-semibold tracking-tightest text-glass-text">Job templates</h1>
        <p className="text-glass-text-2 mt-1">Create campaigns from saved briefs. Seed includes 7 starter templates after `pnpm prisma db seed`.</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>New template</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Input placeholder="Template name" value={name} onChange={(e) => setName(e.target.value)} />
          <Input placeholder="Default job title" value={title} onChange={(e) => setTitle(e.target.value)} />
          <Textarea placeholder="Default description" rows={4} value={description} onChange={(e) => setDescription(e.target.value)} />
          <Button onClick={createTemplate} disabled={!agencyOrgId}>
            Save template
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Use template</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="text-sm text-glass-text-2">Brand for new job</label>
            <Select value={createCorp} onChange={(e) => setCreateCorp(e.target.value)}>
              <option value="">Select…</option>
              {brands.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
          </div>
          <ul className="space-y-2">
            {templates.map((t) => (
              <li key={t.id} className="flex justify-between items-center border border-glass bg-glass-2 rounded-xl p-3">
                <div>
                  <div className="font-medium text-glass-text">{t.name}</div>
                  <div className="text-sm text-glass-text-2">{t.title}</div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => createJobFrom(t.id)}>
                    Create job
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={async () => {
                      if (confirm('Delete template?')) {
                        await api.delete(`/job-templates/${t.id}`);
                        load();
                      }
                    }}
                  >
                    Delete
                  </Button>
                </div>
              </li>
            ))}
          </ul>
          {templates.length === 0 && <p className="text-sm text-glass-text-2">No templates yet.</p>}
        </CardContent>
      </Card>
    </div>
  );
}
